import { AgendaSettingsProvider, useAgendaSettingsContext } from './Context';
import AgendaSettingsGeral from './Geral';
import AgendaSettingsHorarios from './Horarios';
import { PermissionsSchedule } from './Permissions';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import { useSearchParams } from 'react-router-dom';
import {
    AGENDA_SETTINGS_SECTIONS,
    DEFAULT_AGENDA_SETTINGS_SECTION,
    AgendaSettingsSectionKey,
} from './sections';

export { AgendaSettingsProvider };

type AgendaSettingsProps = {
    actionName?: string;
    inlineMenu?: boolean;
    showSectionMenu?: boolean;
    sectionKeys?: AgendaSettingsSectionKey[];
    title?: string;
    description?: string;
    // Dentro de um Modal: o titulo fica no cabecalho dele, e o menu lateral nao esta acessivel.
    inSheet?: boolean;
};

export const AgendaSettings = ({
    actionName = 'settings',
    inlineMenu = false,
    showSectionMenu = true,
    sectionKeys,
    title = 'Configurações da agenda',
    description = 'Defina regras de disponibilidade, horários e permissões do agendamento.',
    inSheet = false,
}: AgendaSettingsProps) => {
    const { user } = useAuthContext();
    const { id } = useParamsContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const { canEditAgendaSettings } = useAgendaSettingsContext();
    const visibleSections = AGENDA_SETTINGS_SECTIONS.filter(
        (section) =>
            (!section.adminOnly || user?.root || user?.admin) &&
            (!sectionKeys || sectionKeys.includes(section.key))
    );
    const activeSection =
        visibleSections.find((section) => section.key === id)?.key || DEFAULT_AGENDA_SETTINGS_SECTION;
    const activeSectionData =
        visibleSections.find((section) => section.key === activeSection) || visibleSections[0];

    const renderSection = (section: AgendaSettingsSectionKey) => {
        switch (section) {
            case 'horarios':
                return <AgendaSettingsHorarios />;
            case 'administrativo':
                return <PermissionsSchedule />;
            case 'geral':
            default:
                return <AgendaSettingsGeral />;
        }
    };

    const changeSection = (section: AgendaSettingsSectionKey) => {
        const nextParams: Record<string, string> = { action: actionName, id: section };
        const attendantId = searchParams.get('attendantId');

        if (attendantId) {
            nextParams.attendantId = attendantId;
        }

        setSearchParams(nextParams);
    };

    const renderSectionMenu = (layout: 'mobile' | 'desktop') => (
        <div
            className={
                layout === 'mobile'
                    ? 'rounded-2xl border border-base-200 bg-base-100/70 p-2'
                    : 'rounded-[1.25rem] border border-base-200 bg-base-100/70 p-2'
            }
        >
            <div className={layout === 'mobile' ? 'grid gap-2 sm:grid-cols-3' : 'space-y-2'}>
                {visibleSections.map((section) => {
                    const isActive = section.key === activeSection;

                    return (
                        <button
                            key={section.key}
                            type="button"
                            className={`w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
                                isActive ? 'bg-primary text-primary-content' : 'text-base-content/70 hover:bg-base-200'
                            }`}
                            onClick={() => changeSection(section.key)}
                        >
                            <section.icon className="mr-2 text-xs" aria-hidden="true" />
                            {section.title}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="mx-auto w-full max-w-5xl space-y-6">
            {!inSheet && (
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold text-base-content">{title}</h1>
                    <p className="text-sm text-base-content/60">{description}</p>
                </div>
            )}

            {!canEditAgendaSettings && (
                <div className="rounded-2xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-base-content/80">
                    {inSheet
                        ? 'Volte para a agenda e selecione um atendente no menu para liberar a edição das configurações.'
                        : 'Selecione um atendente no menu lateral para liberar a edição das configurações.'}
                </div>
            )}

            {showSectionMenu && <div className="lg:hidden">{renderSectionMenu('mobile')}</div>}

            <div className={inlineMenu ? 'grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]' : 'block'}>
                {showSectionMenu && inlineMenu && <div className="hidden lg:block">{renderSectionMenu('desktop')}</div>}

                <div className="space-y-5">
                    <div className="flex flex-col gap-3 border-b border-base-200/70 pb-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                                <activeSectionData.icon className="text-base" aria-hidden="true" />
                            </span>
                            <div>
                                <div className="text-lg font-semibold text-base-content">{activeSectionData.title}</div>
                                <div className="text-sm text-base-content/60">{activeSectionData.description}</div>
                            </div>
                        </div>
                    </div>

                    {renderSection(activeSection)}
                </div>
            </div>
        </div>
    );
}

export default function _({ inSheet = false }: { inSheet?: boolean }) {
    return (
        <AgendaSettingsProvider>
            <AgendaSettings inSheet={inSheet} />
        </AgendaSettingsProvider>
    );
}
